// src/pages/chat/chat.styled.ts
import styled from "@emotion/styled";
import { tablet } from "../../media";

export const ChatWrapper = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overscroll-behavior: none;

    /* Background */
    background: #1a1a1c;

    ${tablet`
        flex-direction: row;
    `}
`;

/* Wraps everything except the sidebar — fills remaining width on desktop */
export const ChatBody = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
`;

export const ChatMain = styled.main`
    flex: 1;
    min-height: 0; /* ✅ critical: allows flex children + overflow to size correctly */
    position: relative; /* ✅ anchors the absolute waveform layer */
    isolation: isolate; /* ✅ predictable z-index layering inside ChatMain */

    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: none; /* prevents iOS bounce / double-scroll */
    -webkit-overflow-scrolling: touch;
    padding: 0 16px;

    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s ease;

    &:hover {
        scrollbar-color: #3a3c44 transparent;
    }

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(58, 60, 68, 0);
        border-radius: 4px;
        transition: background 0.3s ease;
    }
    &:hover::-webkit-scrollbar-thumb {
        background: #3a3c44;
    }
    &:hover::-webkit-scrollbar-thumb:hover {
        background: #4a4c54;
    }

    /* Flex thirds layout */
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    ${tablet`
        align-items: center;
    `}
`;

/* Constrains main content to 720px on desktop, full-width on mobile */
export const ContentConstraint = styled.div`
    width: 100%;

    ${tablet`
        max-width: 720px;
    `}
`;

export const CenterContent = styled.div`
    flex: 1;
    min-height: 0; /* ✅ prevents percentage/auto height weirdness in flex */
    height: 100%;

    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;

    position: relative;
    z-index: 1; /* ✅ keeps main content above the waveform (if desired) */

    box-sizing: border-box;
`;

/* 3 vertical sections */
export const TopThird = styled.div`
    flex: 0 0 auto;

    display: flex;
    justify-content: center;
    align-items: flex-start;

    position: relative;
    z-index: 1; /* ✅ ensure it stays above absolute background effects */

    ${tablet`
        width: 100%;
        max-width: 720px;
        margin: 0 auto;
    `}
`;

export const MiddleThird = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;

    display: flex;
    justify-content: center;
    align-items: center;

    position: relative;
    z-index: 1;
`;

export const BottomThird = styled.div`
    flex: 1;
    min-height: 0;

    position: relative;
    z-index: 1;
`;

export const BottomFade = styled.div`
    pointer-events: none;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;

    /* fade behind dock + a bit above it */
    height: calc(env(safe-area-inset-bottom, 0px) + 170px);

    background: linear-gradient(
        to bottom,
        rgba(7, 10, 18, 0) 0%,
        rgba(7, 10, 18, 0.55) 35%,
        rgba(7, 10, 18, 0.92) 72%,
        rgba(7, 10, 18, 1) 100%
    );
`;

/* ── Sending overlay (matches onboarding question.tsx pattern) ── */

export const SendingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    ${tablet`
        left: var(--sidebar-width, 0px);
    `}
    background: rgba(26, 26, 28, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 9998;
    gap: 20px;
`;

export const SendingLabel = styled.span`
    color: #8f95a3;
    font-size: 15px;
    font-family: Inter, sans-serif;
    font-weight: 500;
    line-height: 20px;
`;

export const SendingProgressTrack = styled.div`
    width: 200px;
    height: 4px;
    background: #2a2a2e;
    border-radius: 9999px;
    overflow: hidden;
`;

export const SendingProgressFill = styled.div<{ progress: number }>`
    height: 100%;
    background: #5e84ed;
    border-radius: 9999px;
    width: ${(props) => props.progress}%;
    transition: width 0.2s ease-out;
`;
